'use client';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Stack,
  Tooltip as MTooltip,
  Typography,
} from '@mui/material';
import { H2, H4, H6, Small } from '../Typography';
import { currencyFormatter, percentFormatter } from '@web/utils/currency';
import NextImage from 'next/image';
import { useState } from 'react';
import { Line, LineChart, ResponsiveContainer, YAxis } from 'recharts';
import { FlexBox } from '@web/components/flex-box';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { manageShopURL } from '@web/utils/link';
import { useAnalytics } from '@web/context/analytics';
import { differenceInDays, isEqual } from 'date-fns';
import { capitalizeFirstLetter } from '@web/utils/string';
import DumbLink from '@web/components/DumbLink';
import SellIcon from '@mui/icons-material/Sell';
import SellerImage from '@web/components/SellerImage';
import {
  TimeRangeControls,
  TimeRange,
} from '@web/app/components/TimeRangeControls';
import { filterByTimeRange } from '@web/utils/timeRange';
import { formatDistanceToNow } from 'date-fns';
import PriceRating from './PriceRating';
import { consolidatePriceHistory } from '@web/utils/priceHistory';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const getPriceStats = (
  priceHistory: { base_price: number; created_at: string }[]
) => {
  if (!priceHistory?.length) return null;

  const prices = priceHistory.map((p) => p.base_price);
  return {
    allTimeHigh: Math.max(...prices),
    allTimeLow: Math.min(...prices),
    average: prices.reduce((a, b) => a + b, 0) / prices.length,
    current: prices[prices.length - 1],
  };
};

const OfferTableMobile = ({ recentOffers, oldOffers, oz, lastDate, offersHistory }) => {
  const [showAll, setShowAll] = useState(false);
  const { trackOfferClick } = useAnalytics();
  const perPage = 5;
  const diffDays = differenceInDays(new Date(), new Date(lastDate));
  const [timeRange, setTimeRange] = useState<TimeRange>('1Y');

  const consolidatedPrices = consolidatePriceHistory(offersHistory);
  const handleClick = (url, seller_id) => {
    trackOfferClick(url, seller_id);
    window.open(manageShopURL(url), '_blank');
  };

  const OfferCard = ({ offer }) => {
    const ozDeals = oz?.filter((oz) => oz.link === offer.seller_product_url);
    const priceHistory = filterByTimeRange(
      offersHistory?.[offer.seller_product_url] || [],
      timeRange
    );

    const currentPrice = priceHistory?.[priceHistory?.length - 1]?.base_price;
    const lastDifferentPrice = priceHistory
      ?.slice(0, -1)
      .reverse()
      .find((p) => p.base_price !== currentPrice)?.base_price;

    const price_off = lastDifferentPrice
      ? 1 - currentPrice / lastDifferentPrice
      : 0;

    const priceStats = getPriceStats(priceHistory);
    const isLatest = isEqual(new Date(offer.created_at), new Date(lastDate));
    return (
      <Card raised>
        <Grid
          container
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={1}
          pr={2}
          onClick={() =>
            handleClick(offer.seller_product_url, offer.seller.seller_id)
          }
        >
          <Grid item xs={2.5}>
            <SellerImage
              seller_product_url={offer.seller_product_url}
              width={64}
              height={40}
            />
            {/*{(offer.seller_name.toLowerCase().includes('ebay') ||*/}
            {/*  offer.seller_name.toLowerCase().includes('amazon')) && (*/}
            {/*  <MTooltip*/}
            {/*    placement="top"*/}
            {/*    title="Affiliate Link: We may receive a small commission for purchases made through this link at no extra cost to you. This helps support our site. Thank you!"*/}
            {/*  >*/}
            {/*    <Chip*/}
            {/*      label="Affiliate"*/}
            {/*      sx={{ borderRadius: 2, width: 90 }}*/}
            {/*      size="small"*/}
            {/*    />*/}
            {/*  </MTooltip>*/}
            {/*)}*/}
          </Grid>
          <Grid item xs={6.3}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack pt={1} flex={1}>
                <H6>{offer.seller.name}</H6>
                <ResponsiveContainer height={40}>
                  <LineChart width={100} height={40} data={priceHistory}>
                    <YAxis domain={['dataMin', 'dataMax']} hide />
                    <Line
                      type="monotone"
                      dot={(props): any => {
                        if (priceStats?.allTimeHigh === priceStats?.allTimeLow)
                          return false;

                        const lastHighIndex = [...priceHistory]
                          .reverse()
                          .findIndex(
                            (p) => p.base_price === priceStats?.allTimeHigh
                          );
                        const lastLowIndex = [...priceHistory]
                          .reverse()
                          .findIndex(
                            (p) => p.base_price === priceStats?.allTimeLow
                          );

                        const highIndex =
                          lastHighIndex === -1
                            ? -1
                            : priceHistory.length - 1 - lastHighIndex;
                        const lowIndex =
                          lastLowIndex === -1
                            ? -1
                            : priceHistory.length - 1 - lastLowIndex;

                        if (
                          props.index !== highIndex &&
                          props.index !== lowIndex
                        )
                          return false;

                        return (
                          <circle
                            key={props.index}
                            cx={props.cx}
                            cy={props.cy}
                            r={3}
                            fill={
                              props.value === priceStats?.allTimeHigh
                                ? '#d32f2f'
                                : '#2e7d32'
                            }
                            stroke="white"
                            strokeWidth={1}
                          />
                        );
                      }}
                      dataKey="base_price"
                      strokeWidth={2}
                      isAnimationActive={false}
                      stroke={
                        price_off === 0
                          ? '#8884d8'
                          : price_off > 0
                          ? 'green'
                          : 'red'
                      }
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Stack>
              <Stack spacing={0.5} ml={1}>
                {price_off !== 0 && !isNaN(price_off) && (
                  <H6 color={price_off > 0 ? 'green' : 'red'}>
                    {price_off > 0 ? '' : '+'}
                    {percentFormatter.format(-price_off)}
                  </H6>
                )}
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={3.2}>
            <Stack>
              <Box sx={{ alignSelf: 'end' }}>
                <H4>{currencyFormatter.format(offer.base_price)}</H4>
              </Box>
              {offer.condition !== 'new' && !!offer.condition && (
                <Chip
                  label={capitalizeFirstLetter(offer.condition)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {(!isLatest || diffDays > 7) && (
                <Chip label="Low Stock" size="small" color="warning" />
              )}
              {!offer.seller_product_url && (
                <Chip
                  label="In store only"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {Number(offer.shipping) !== 0 && isLatest && (
                <Box sx={{ alignSelf: 'end' }}>
                  <Small>
                    +{currencyFormatter.format(offer.shipping)} delivery
                  </Small>
                </Box>
              )}
              {/* {priceStats &&
                  priceStats.allTimeHigh !== priceStats.allTimeLow && (
                    <Stack>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#d32f2f',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontWeight:
                            currentPrice === priceStats.allTimeHigh ? 600 : 400,
                        }}
                      >
                        H: {currencyFormatter.format(priceStats.allTimeHigh)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#2e7d32',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontWeight:
                            currentPrice === priceStats.allTimeLow ? 600 : 400,
                        }}
                      >
                        L: {currencyFormatter.format(priceStats.allTimeLow)}
                      </Typography>
                    </Stack>
                  )} */}
            </Stack>
          </Grid>
        </Grid>
        {offer.coupon && (
          <Stack>
            <>
              <Divider />
              <Stack direction="row" gap={2} alignItems="center">
                <Typography pl={4} py={0.5}>
                  Coupon:{' '}
                </Typography>
                <Grid container spacing={2}>
                  {offer.coupon.split(',').map((coupon, index) => (
                    <Grid item key={index}>
                      <Chip
                        label={
                          <Typography fontWeight={600}>{coupon}</Typography>
                        }
                        variant="outlined"
                        size="small"
                        icon={<SellIcon />}
                        sx={{
                          borderRadius: 2,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </>
          </Stack>
        )}
        {offer.hm_mpd_rate &&
          !(
            offer.seller_name.toLowerCase().includes('ebay') ||
            offer.seller_name.toLowerCase().includes('amazon')
          ) && (
            <>
              <Divider />
              <FlexBox
                alignItems={'center'}
                flexBasis={250}
                component={DumbLink}
                href={`https://heymax.ai/merchant/au/${offer.hm_name}?utm_source=buywisely&utm_medium=discount`}
                target="_blank"
                sx={{
                  pl: 2,
                  py: 0.5,
                }}
              >
                <NextImage
                  src="/assets/images/heymax.png"
                  width={100}
                  height={16}
                  alt="heymax"
                />
                <Stack spacing={0.5}>
                  {offer.hm_mpd_rate?.map((rate, index) => (
                    <Typography key={index} color="secondary" sx={{ px: 1 }}>
                      {rate.name} <strong>{rate.value} miles / AUD</strong>
                    </Typography>
                  ))}
                </Stack>
              </FlexBox>
            </>
          )}
        {offer.seller.cashrewards &&
          !(
            offer.seller.name.toLowerCase().includes('ebay') ||
            offer.seller.name.toLowerCase().includes('amazon')
          ) && (
            <>
              <Divider />
              <FlexBox
                alignItems={'center'}
                flexBasis={250}
                component={DumbLink}
                href={`https://www.cashrewards.com.au/store/${offer.seller.cashrewards.hyphenated_name}?utm_source=buywisely`}
                target="_blank"
                sx={{
                  pl: 2,
                  py: 0.5,
                }}
              >
                <NextImage
                  src="/assets/images/cashrewards.png"
                  width={100}
                  height={16}
                  alt="cashrewards"
                />
                <Typography color="secondary" variant="body2" sx={{ pl: 1 }}>
                  {offer.seller.cashrewards.commission_string}
                </Typography>
              </FlexBox>
            </>
          )}
        {offer.seller.shopback &&
          offer.seller.shopback.url &&
          !(
            offer.seller.name.toLowerCase().includes('ebay') ||
            offer.seller.name.toLowerCase().includes('amazon')
          ) && (
            <>
              <Divider />
              <FlexBox
                alignItems={'center'}
                flexBasis={250}
                component={DumbLink}
                href={`https://${offer.seller.shopback.url}?utm_source=buywisely`}
                target="_blank"
                sx={{
                  pl: 2,
                  py: 0.5,
                }}
              >
                <NextImage
                  src="/assets/images/shopback.png"
                  width={100}
                  height={25}
                  alt="shopback"
                />
                <Typography color="secondary" variant="body2" sx={{ pl: 1 }}>
                  {offer.seller.shopback.commission}
                </Typography>
              </FlexBox>
            </>
          )}
        {ozDeals?.map((oz) => (
          <Box key={oz.node}>
            <Divider />
            <FlexBox
              alignItems={'center'}
              flexBasis={250}
              component={DumbLink}
              href={`https://www.ozbargain.com.au/node/${oz.node}?utm_source=buywisely`}
              target="_blank"
              sx={{
                px: 2,
                py: 0.5,
              }}
            >
              <NextImage
                src={`/images/oz.webp`}
                width={20}
                height={20}
                alt="ozbargain"
                style={{ objectFit: 'contain' }}
              />
              <Stack direction="row" gap={1} alignItems="center" px={1}>
                <Typography fontWeight="600" color="#4a4">
                  {oz.upvotes}
                </Typography>
                <Typography fontWeight="600" color="#c30">
                  {oz.downvotes}
                </Typography>
                {oz.status && (
                  <Chip
                    label={oz.status}
                    size="small"
                    color="error"
                    sx={{ fontWeight: 700 }}
                  />
                )}
              </Stack>
              <Typography color="secondary" variant="body2">
                {oz.title}
              </Typography>
            </FlexBox>
          </Box>
        ))}
        {/*{(offer.seller.name.toLowerCase().includes('ebay') ||*/}
        {/*  offer.seller.name.toLowerCase().includes('amazon')) && (*/}
        {/*  <>*/}
        {/*    <Divider />*/}
        {/*    <Typography px={2} py={1}>*/}
        {/*      <strong>Affiliate Disclosure:</strong> We may receive a small*/}
        {/*      commission for purchases made through this link at no extra cost*/}
        {/*      to you. This helps support our site. Thank you!*/}
        {/*    </Typography>*/}
        {/*  </>*/}
        {/*)}*/}
      </Card>
    );
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        py={1}
      >
        <Stack>
          <H2 fontSize="1.25rem">Price comparison</H2>
          {lastDate && (
            <Small>Updated {formatDistanceToNow(new Date(lastDate))} ago</Small>
          )}
        </Stack>

        <TimeRangeControls value={timeRange} onChange={setTimeRange} />
      </Stack>

      <Box mb={2}>
        <PriceRating
          currentPrice={consolidatedPrices.currentPrice}
          priceHistory={consolidatedPrices.history}
          timeRange={timeRange}
          viewport="mobile"
        />
      </Box>

      <Stack spacing={2}>
        {(showAll ? recentOffers : recentOffers.slice(0, perPage)).map(
          (offer, index) => (
            <Box key={index}>
              <OfferCard offer={offer} />
            </Box>
          )
        )}
      </Stack>
      {!showAll && perPage < recentOffers.length && (
        <Button
          variant="text"
          size="large"
          fullWidth
          endIcon={<KeyboardArrowDownIcon />}
          onClick={() => setShowAll(true)}
        >
          View {recentOffers.length - perPage} More
        </Button>
      )}
      {oldOffers.length > 0 && (
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontSize={16} fontWeight={600}>
              See {oldOffers.length} more history offers
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: 'rgba(0, 0, 0, .03)',
            }}
          >
            <Stack spacing={2}>
              {oldOffers.map((offer, index) => (
                <OfferCard key={index} offer={offer} />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
export default OfferTableMobile;
